'use client';

import * as React from 'react';
import { CheckCircle, Loader2, Send, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { type BatchActionItem } from '@/components/ui/workflow';
import type { LawFirmUser } from '@/lib/services/account/types';
import { getUserDisplayName } from '@/lib/utils/permissions';
import type { WorkflowInstitution } from '@/lib/services/correspondences/workflows/workflow-types';
import { transformDocument } from '@/lib/services/correspondences/workflows/transformers';
import { SenderSignatoryDisclosure } from '@/components/shared/sender-signatory-disclosure';
import {
  InstitutionSendCard,
  type SignatoryUser
} from '../../notifications-workflow/components/institution-send-card';
import type { SendStepConfig } from './send-step.types';
import type { useSendStep } from './use-send-step';
import { SENDER_COPY_LABEL } from '@/lib/services/integrations/outlook';

const POST_ONLY_TOOLTIP_MESSAGE =
  'This correspondence has been marked to be sent via post only';

type SendStepViewProps = {
  config: SendStepConfig;
  state: ReturnType<typeof useSendStep>;
};

export function SendStepView({ config, state }: SendStepViewProps) {
  const {
    institutions,
    selectedInstitutions,
    signatories,
    documents,
    correspondenceByInstitution,
    isCreatingDrafts,
    localPendingResearchIds,
    additionalDocs,
    emailTos,
    sendingStatus,
    isSendingAll,
    externalIsSending,
    masterSignatoryId,
    handleMasterSignatoryChange,
    sendSenderCopy,
    setSendSenderCopy,
    outlookSendsFromMe,
    senderSignatoryDisclosure,
    stats,
    allCorrespondencesValid,
    allSelected,
    allSelectedArePostOnly,
    sendResults,
    initialMatterId,
    handleSendSingle,
    handleSendAll,
    handleUpdateLetterContent,
    handleEmailToChange,
    handleAddDocument,
    handleAddCorrespondenceAttachment,
    handleRemoveDocument,
    handleRemoveCorrespondenceAttachment,
    handleUploadDocument,
    handleChangeSignatory,
    handleToggleInstitution,
    handleToggleAll,
    handleToggleSendViaPostOnly
  } = state;

  if (isCreatingDrafts) {
    return (
      <div className="flex h-full items-center justify-center py-4">
        <div className="space-y-2 text-center">
          <div className="border-primary mx-auto h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-muted-foreground text-sm">
            {config.loadingMessage}
          </p>
        </div>
      </div>
    );
  }

  const masterSignatoryDisabled = stats.sent === selectedInstitutions.length;

  // `SignatoryUser` now honestly declares firstName/lastName as `string | null`
  // (LF-6056), matching `LawFirmUser` — no boundary coercion needed.
  const signatoryOptions: SignatoryUser[] = signatories;

  return (
    <div className="space-y-6">
      {selectedInstitutions.length === 0 && (
        <Alert>
          <AlertDescription>
            No institutions selected. Please select at least one institution
            using the checkboxes to send {config.resultsItemLabel}.
          </AlertDescription>
        </Alert>
      )}

      {sendResults && (
        <Alert
          variant={sendResults.failed.length > 0 ? 'destructive' : 'default'}
        >
          {sendResults.failed.length > 0 ? (
            <XCircle className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          <AlertDescription>
            {sendResults.successful.length > 0 && (
              <div>
                Successfully sent {sendResults.successful.length}{' '}
                {config.resultsItemLabel}
              </div>
            )}
            {sendResults.failed.length > 0 && (
              <div>
                Failed to send {sendResults.failed.length}{' '}
                {config.resultsItemLabel}:
                <ul className="mt-1 list-inside list-disc">
                  {sendResults.failed.map(({ id, error }) => (
                    <li key={id} className="text-sm">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-4">
          {config.header ? (
            <div>
              <h3 className="text-base font-medium">
                {config.header.title(selectedInstitutions.length)}
              </h3>
              <p className="text-muted-foreground text-sm">
                {config.header.description}
              </p>
            </div>
          ) : (
            <div />
          )}

          {allSelectedArePostOnly ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button size="xs" density="compact" gap="standard" disabled>
                      <Send className="h-3.5 w-3.5" />
                      {config.sendAllButtonLabel}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{POST_ONLY_TOOLTIP_MESSAGE}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              size="xs"
              density="compact"
              gap="standard"
              onClick={handleSendAll}
              disabled={
                !allCorrespondencesValid ||
                stats.pending === 0 ||
                isSendingAll ||
                externalIsSending
              }
            >
              {isSendingAll || externalIsSending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  {config.sendAllButtonLabel}
                </>
              )}
            </Button>
          )}
        </div>

        <Card className="border-muted">
          <CardContent className="py-4">
            <div className="flex flex-wrap justify-end md:flex-nowrap md:space-x-5">
              <div className="order-2 mt-3 flex items-center space-x-6 md:order-1 md:mt-auto md:pb-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={allSelected}
                    onCheckedChange={handleToggleAll}
                  />
                  <label
                    htmlFor="select-all"
                    className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Select all
                  </label>
                </div>

                {/* Hidden only when the acting user IS the mailbox that will
                    send — otherwise the sender needs their own copy (LF-5664). */}
                {!outlookSendsFromMe &&
                  (allSelectedArePostOnly ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center space-x-2">
                            <Checkbox id="receive-copy" disabled />
                            <label
                              htmlFor="receive-copy"
                              className="cursor-not-allowed text-sm leading-none font-medium opacity-70"
                            >
                              {SENDER_COPY_LABEL}
                            </label>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {POST_ONLY_TOOLTIP_MESSAGE}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="receive-copy"
                        checked={sendSenderCopy}
                        onCheckedChange={(checked) =>
                          setSendSenderCopy(checked as boolean)
                        }
                        disabled={masterSignatoryDisabled}
                      />
                      <label
                        htmlFor="receive-copy"
                        className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {SENDER_COPY_LABEL}
                      </label>
                    </div>
                  ))}
              </div>

              <div className="order-1 w-full space-y-1 md:order-2 md:max-w-sm">
                <label className="text-muted-foreground text-xs">
                  Signatory for all institutions
                </label>
                <Select
                  value={masterSignatoryId}
                  onValueChange={handleMasterSignatoryChange}
                  disabled={masterSignatoryDisabled}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="Select a signatory" />
                  </SelectTrigger>
                  <SelectContent>
                    {signatories.map((signatory: LawFirmUser) => (
                      <SelectItem
                        key={signatory.id}
                        value={signatory.id.toString()}
                      >
                        <div className="flex w-full items-center justify-between">
                          <span className="text-sm">
                            {/* LF-6015: `.trim()` here was dead — `'null null'`
                                is truthy — so a nameless signatory read as
                                `null null`. The canonical helper filters the
                                nulls and falls back to the email. */}
                            {getUserDisplayName(signatory)} - {signatory.role}
                          </span>
                          {/* A missing signature is a state of the signatory
                              (it gates the send), so a caution StatusBadge —
                              `subtle` because it sits inside a select row. */}
                          {!signatory.handwrittenSignatureFile?.id && (
                            <StatusBadge
                              tone="caution"
                              emphasis="subtle"
                              className="ml-2"
                            >
                              No signature
                            </StatusBadge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Signatory ≠ sender disclosure. Banks and registries
                    increasingly ring the solicitor to authenticate
                    correspondence, so the person sending must be able to see
                    that the letter will carry someone else's signature over
                    someone else's address (LF-5664). Disclosure, not a
                    warning — it never blocks the send. */}
                {!!senderSignatoryDisclosure && (
                  <SenderSignatoryDisclosure
                    senderName={senderSignatoryDisclosure.senderName}
                    signatoryName={senderSignatoryDisclosure.signatoryName}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {institutions.map((inst: WorkflowInstitution) => {
            const correspondence = correspondenceByInstitution[inst.id];
            const currentSignatoryId = correspondence?.signatoryUser?.email
              ? signatories
                  .find((s) => s.email === correspondence.signatoryUser!.email)
                  ?.id?.toString() || ''
              : '';

            const isCollectionWorkflow = config.supportsPostOnly;

            return (
              <InstitutionSendCard
                key={inst.id}
                institution={inst}
                correspondence={{
                  item: {
                    id: inst.id.toString(),
                    status:
                      localPendingResearchIds.has(inst.id) ||
                      correspondence?.status === 'pending-research-email'
                        ? 'pending'
                        : correspondence?.validation.isValid
                          ? sendingStatus[inst.id.toString()] || 'pending'
                          : 'blocked'
                  } as BatchActionItem,
                  letterContent: correspondence?.correspondenceLetterBody || '',
                  emailSubject: correspondence?.subject || '',
                  emailBody: correspondence?.fragments?.mainContent || '',
                  additionalDocs: additionalDocs[inst.id.toString()] || [],
                  availableDocuments:
                    correspondence?.attachments?.map(transformDocument) || [],
                  matterDocuments: documents,
                  correspondenceId: correspondence?.id,
                  validation: correspondence?.validation,
                  status: localPendingResearchIds.has(inst.id)
                    ? 'pending-research-email'
                    : correspondence?.status,
                  emailTo:
                    emailTos[inst.id.toString()] ||
                    correspondence?.emailto ||
                    '',
                  requiresEmailResearch: !inst.emailAddress
                }}
                signatoryState={{
                  current: currentSignatoryId,
                  options: signatoryOptions
                }}
                matter={{ id: initialMatterId || 0 }}
                selection={{
                  isSelected: inst.selected,
                  sendViaPostOnly:
                    isCollectionWorkflow &&
                    (correspondence?.sendViaPostOnly || false)
                }}
                mode={{ collectionWorkflow: isCollectionWorkflow }}
                actions={{
                  onUpdateLetterContent: handleUpdateLetterContent,
                  onAddDocument: handleAddDocument,
                  onUploadDocument: handleUploadDocument,
                  onAddCorrespondenceAttachment:
                    handleAddCorrespondenceAttachment,
                  onRemoveDocument: handleRemoveDocument,
                  onRemoveCorrespondenceAttachment:
                    handleRemoveCorrespondenceAttachment,
                  onChangeSignatory: handleChangeSignatory,
                  onSend: () =>
                    handleSendSingle({
                      id: inst.id.toString(),
                      title: inst.name,
                      description: '',
                      status: correspondence?.validation.isValid
                        ? sendingStatus[inst.id.toString()] || 'pending'
                        : 'blocked',
                      data: inst
                    } as BatchActionItem),
                  onToggle: handleToggleInstitution,
                  onEmailToChange: handleEmailToChange,
                  onToggleSendViaPostOnly: isCollectionWorkflow
                    ? handleToggleSendViaPostOnly
                    : undefined
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
